import { t } from '@/utils/i18n'
import { useRef, useState, useEffect, useMemo } from 'react'
import { useAliveController } from 'react-activation'
import { useSetState, useMount, useCountDown } from 'ahooks'
import { Button, Popover, Input, Toast, Checkbox, Tabs } from 'antd-mobile'
import { RightOutline, DownOutline } from 'antd-mobile-icons'
import { Action } from 'antd-mobile/es/components/popover'
import { useParams, useNavigate } from 'react-router-dom'
import { apis, utils, common, routesMap, storageUtil } from '@/utils'
import exchangeTokenUtil from '@/utils/token'
import { useCorpSettingStore } from '@/stores'
import paifaLogoText from '@/assets/img/icon/paifa-logo-text.png'
import arrowDown from '@/assets/img/icon/arrow-down.png'
import { Cart } from '@/types/cart'
import styles from './index.module.less'
import { getI18nLang, getSystemLang, langOptions } from '@/utils/i18n'
import i18n from '@/utils/i18n'
import ZoneNumberList from './components/ZoneNumberList'
import type { LoginForm, PageType, ICountryNumber, ISiteItem } from './types'

const { formatMD5, to, getUuid } = utils
const { getLocalStorage, setLocalStorage, removeLocalStorage } = storageUtil
const {
  handleCorpSetting,
  hasHomeModule,
  storeCorpInfo,
  storeCorpInfoTempLogin,
} = common
const { authApi, contactApi, basicApi, orderApi } = apis
const phoneReg = /^(\+\d{1,3}[- ]?)?\d{9,15}$/
const mailReg = /^([\w-_]+(?:\.[\w-_]+)*)@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/
const passwordReg = /^.{4,16}$/
enum AccountType {
  PHONE = '1',
  EMAIL = '2',
}

function Login() {
  const { corpSetting, setCorpSetting } = useCorpSettingStore()
  const { pageType: _pageType = 'login' } = useParams()
  const navigate = useNavigate()
  const { clear } = useAliveController() // getCachingNodes 获取当前所有缓存节点

  const inputRef = useRef<any>(null)
  const [pageType, setPageType] = useState<PageType>(_pageType as PageType)
  const [isAgree, setIsAgree] = useState(false)
  const [formData, setFormData] = useSetState<LoginForm>({
    account: '',
    password: '',
    passwordAgain: '',
    code: '',
  })
  const [countryList, setCountryList] = useState<ICountryNumber[]>([])
  const [zoneNumberVisible, setZoneNumberVisible] = useState(false)
  const [zoneNumber, setZoneNumber] = useState<ICountryNumber>({
    countryId: '',
    countryName: '',
    gecCode: '',
    telephoneCode: '',
  })
  const btnText = useMemo(() => {
    if (pageType === 'register') {
      return t('注册')
    } else if (pageType === 'login') {
      return t('登录')
    } else {
      return t('重置密码')
    }
  }, [pageType])
  const passwordMD5 = useMemo(
    () => formatMD5(formData.password),
    [formData.password],
  )
  const passwordAgainMD5 = useMemo(
    () => formatMD5(formData.passwordAgain),
    [formData.passwordAgain],
  )
  const [sendCodeType, setSendCodeType] = useState('init')
  const [targetDate, setTargetDate] = useState(0)
  const [accountType, setAccountType] = useState('1')
  const [sendTime] = useCountDown({
    targetDate,
    onEnd: () => setSendCodeType('sended'),
  })
  const codeText = useMemo(() => {
    if (sendCodeType === 'init') {
      return t('获取验证码')
    } else if (sendCodeType === 'sended') {
      return t('重新发送')
    } else {
      return `${Math.round(sendTime / 1000)}s`
    }
  }, [sendTime, sendCodeType])
  const inputPassWordText =
    pageType === 'forgetPassword'
      ? t('请输入新密码（4-16位）')
      : pageType === 'register'
      ? t('请输入密码（4-16位）')
      : t('请输入密码')

  const inputAccountText =
    pageType === 'forgetPassword'
      ? t('请输入手机号')
      : accountType === AccountType.PHONE
      ? t('请输入手机号')
      : t('请输入邮箱')
  const inputPassWordTextAgain = t('请再次输入密码')
  const [langOpt, setLangOpt] = useState<Action>({
    text: '中文',
    key: 'zh-CN',
  })

  useMount(async () => {
    setTimeout(() => {
      inputRef.current.focus()
    }, 0)
    clear() // 清除所有 keepalive 缓存
    removeLoginStorage()
    initUuId()
    initLang()
    getZoneNumber()
  })

  useEffect(() => {
    if (pageType === 'forgetPassword') {
      setFormData({
        password: '',
        passwordAgain: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType])

  // 根据app传入的phoneCountry，初始化区号
  function initZoneNumber(list = countryList, currentCountryId = '') {
    if (!currentCountryId) return
    const zoneNumberData = list.find(
      (item) => item.countryId === currentCountryId,
    )
    if (zoneNumberData) {
      setZoneNumber(zoneNumberData)
      setLocalStorage('zoneNumber', zoneNumberData.telephoneCode)
    }
  }

  // 初始化语言选择，有存储就读存储，否则系统默认
  function initLang() {
    const localLanguage = getLocalStorage('language')
    const language = localLanguage || getSystemLang()
    if (!localLanguage) setLocalStorage('language', language)
    const langOptItem = langOptions.find((item) => item.key === language) || {
      text: 'English',
      key: 'en-US',
    }
    setLangOpt(langOptItem)
    corpSetting.language = langOptItem.key
    setCorpSetting(corpSetting)
    const i18nLang = getI18nLang()
    i18n.changeLanguage(i18nLang)
  }

  // 初始化uuid
  function initUuId() {
    const uuid = getLocalStorage('uuid')
    if (!uuid) {
      setLocalStorage('uuid', getUuid())
    }
  }

  // 清除存储信息
  function removeLoginStorage() {
    removeLocalStorage('token')
    removeLocalStorage('corpInfo')
    sessionStorage.clear()
  }

  /*
   * 选择站点
   */
  const [siteList, setSiteList] = useState<Action[]>([])
  const [siteOpt, setSiteOpt] = useState<Action>()

  // 获取站点列表
  async function getSiteList(currentCountryId = '') {
    const [err, res] = await to(basicApi.getSiteList())

    if (err || !res) {
      return
    }

    const resData = res.data || []

    // 根据多语言格式化数据
    let siteNameKey = 'siteName'
    if (corpSetting.language === 'en-US') {
      siteNameKey = 'siteNameEn'
    }
    if (corpSetting.language === 'vi-VN') {
      siteNameKey = 'siteNameVi'
    }
    const actionList: Action[] = resData.map((item: ISiteItem) => {
      return {
        key: item.corpId,
        text: item[siteNameKey],
        corpId: item.corpId,
        countryId: item.countryId,
      }
    })

    setSiteList(actionList)

    // 设置当前站点
    const currentSite = resData.find((item: ISiteItem) => {
      return item.countryId === currentCountryId
    })
    if (currentSite) {
      setSiteOpt(actionList.find((item) => item.key === currentSite.corpId))
    } else if (actionList.length) {
      setSiteOpt(actionList[0])
    }
  }

  /*
   * 国家和区号
   */
  // 获取国家和区号
  async function getZoneNumber() {
    const [err, res] = await to(basicApi.getCountryList())

    if (err || !res) {
      return
    }

    const resData = res.data || {}
    setCountryList(resData)

    let currentCountryId = ''

    // 如果有已选的则不切换
    const sessionStorageSite = sessionStorage.getItem('site') || ''
    if (sessionStorageSite) {
      currentCountryId = sessionStorageSite
    }

    // Invoice Maker Pro 从App中获取国家
    if (!currentCountryId && window.webview?.getAuth) {
      try {
        const auth = JSON.parse(window.webview.getAuth())
        currentCountryId = auth.countryId
      } catch (e) {
        console.log(e)
      }
    }
    // Mall App 从接口获取国家
    if (!currentCountryId) {
      const resExtends = res.extend || {}
      currentCountryId = currentCountryId
        ? currentCountryId
        : resExtends.currentCountryId
    }

    // 初始化区号和站点
    initZoneNumber(resData, currentCountryId)
    getSiteList(currentCountryId)
  }

  // 选择语言
  async function onSelectLang(action: Action) {
    setLangOpt(action)
    corpSetting.language = action.key
    setCorpSetting(corpSetting)
    setLocalStorage('language', action.key)
    window.webview?.switchLanguage?.(
      String(action.key)?.replace('-', '_') || '',
    )
    const i18nLang = getI18nLang()
    i18n.changeLanguage(i18nLang)
    // navigate(0);
    getZoneNumber()
  }

  // 切换站点的时候，选择语言
  const setSite = async (action: Action) => {
    setSiteOpt(action)
    setLocalStorage('site', action.key)
    sessionStorage.setItem('site', (action as any).countryId)

    const countryId = (action as any)?.countryId
    if (!countryId) {
      return
    }

    // 沙特、阿联酋、印度站点，选择英文
    let langOptItem = langOptions.find((item) => item.key === 'en-US') || {
      text: 'English',
      key: 'en-US',
    }
    // 越南站点，选择越南文
    if (countryId === '22094') {
      langOptItem = langOptions.find((item) => item.key === 'vi-VN') || {
        text: 'Vietnamese',
        key: 'vi-VN',
      }
    }
    if (langOpt.key === langOptItem.key) {
      return
    }

    onSelectLang(langOptItem)
  }

  // 获取验证码
  async function getCode() {
    if (sendCodeType === 'sending' && sendTime >= 0) return

    if (!formData.account) {
      Toast.show(t('请输入手机号'))
      return
    }

    setSendCodeType('sending')
    setTargetDate(Date.now() + 60 * 1000)
    authApi.notifySms({
      phone: formData.account,
      type: 3, // 3改账号
    })
  }

  // 点击确认按钮
  function onConfirm() {
    if (!formData.account) {
      Toast.show(t('请输入手机或邮箱'))
      return
    }

    if (pageType === 'register') {
      register()
    } else if (pageType === 'login') {
      login()
    } else {
      forgetPassword()
    }
  }

  // 处理注册
  async function register() {
    const { password, passwordAgain, account } = formData

    if (accountType === AccountType.PHONE && !phoneReg.test(account)) {
      Toast.show(t('请输入正确的手机号'))
      return
    }

    if (accountType === AccountType.EMAIL && !mailReg.test(account)) {
      Toast.show(t('请输入正确的邮箱号'))
      return
    }

    if (!password || !passwordAgain) {
      Toast.show(t('请输入密码和确认密码'))
      return
    }

    if (!passwordReg.test(password) || !passwordReg.test(passwordAgain)) {
      Toast.show(t('密码为4~16位字符'))
      return
    }

    if (password !== passwordAgain) {
      Toast.show(t('两次输入的密码不一致'))
      return
    }

    if (!isAgree) {
      Toast.show(t('请先阅读并勾选《用户协议》和《隐私政策》'))
      return
    }

    const corpId = (siteOpt as any)?.corpId || ''
    const countryId = (siteOpt as any)?.countryId || ''
    if (!corpId) {
      Toast.show(t('请选择站点'))
      return
    }

    const [err, res] = await to(
      authApi.paifaMallRegister({
        data: account,
        password: passwordMD5,
        verifyPassword: passwordAgainMD5,
        phoneCountry: zoneNumber.telephoneCode,
        type: Number(accountType),
        corpId,
      }),
    )

    if (err || !res) {
      return
    }
    const resData = Object.assign(res.data || {}, { countryId })
    const { bigToken, pfUser, cart } = resData
    const { accountId, id } = pfUser || {}
    storeCorpInfo(resData)
    // updataConfig(resData)
    validateCart(corpId, cart)
    exchangeTokenUtil.setExchangeRequestParams({
      bigToken,
      accountId,
      id,
      corpId,
    })

    const homeModule = await hasHomeModule()
    if (homeModule) {
      navigate(routesMap.App, {
        replace: true,
      })
    } else {
      navigate(routesMap.Products, {
        replace: true,
      })
    }
  }

  // 处理登录
  async function login() {
    if (!isAgree) {
      Toast.show(t('请先阅读并勾选《用户协议》和《隐私政策》'))
      return
    }

    const corpId = (siteOpt as any)?.corpId || ''
    const countryId = (siteOpt as any)?.countryId || ''
    if (!corpId) {
      Toast.show(t('请选择站点'))
      return
    }

    const params: any = {
      account: formData.account,
      password: passwordMD5,
      type: Number(accountType),
      corpId,
    }
    // 如果是手机登录，才传国家号
    if (params.type === Number(AccountType.PHONE))
      params.phoneCountry = zoneNumber.telephoneCode
    const [err, res] = await to(authApi.paifaMallLogin(params))

    if (err || !res) {
      return
    }
    const resData = Object.assign(res.data || {}, { countryId })
    const { bigToken, pfUser, cart } = resData
    const { accountId, id } = pfUser || {}
    storeCorpInfo(resData)
    // updataConfig(resData)
    validateCart(corpId, cart)
    exchangeTokenUtil.setExchangeRequestParams({
      bigToken,
      accountId,
      id,
      corpId,
    })

    const homeModule = await hasHomeModule()
    if (homeModule) {
      navigate(routesMap.App, {
        replace: true,
      })
    } else {
      navigate(routesMap.Products, {
        replace: true,
      })
    }
  }

  // 登陆完后，校验购物车是否有货品冲突
  function validateCart(corpId: string, cart: Cart) {
    const { cartId } = cart || {}
    orderApi.shoppingCartValidateWholeCart(
      {
        cartId,
        checkType: 1, // 1首次进入购物车检测 2点击下单检测 3结账检测
        corpId,
      },
      { showMessage: false },
    )
  }

  // 临时登录
  async function onTempLogin() {
    const corpId = (siteOpt as any)?.corpId || ''
    const [err, res] = await to(
      authApi.paifaMallTempLogin({
        corpId,
      }),
    )

    if (err || !res) {
      return
    }

    const resData = res.data || {}

    storeCorpInfoTempLogin(resData)
    // updataConfig(resData)

    const homeModule = await hasHomeModule()
    if (homeModule) {
      navigate(routesMap.App, {
        replace: true,
      })
    } else {
      navigate(routesMap.Products, {
        replace: true,
      })
    }
  }

  // 处理忘记密码，点确定
  async function forgetPassword() {
    const { code, account, password, passwordAgain } = formData
    if (!code) {
      Toast.show(t('请输入验证码'))
      return
    }

    if (!password || !passwordAgain) {
      Toast.show(t('请输入密码和确认密码'))
      return
    }

    if (!passwordReg.test(password) || !passwordReg.test(passwordAgain)) {
      Toast.show(t('密码为4~16位字符'))
      return
    }

    if (password !== passwordAgain) {
      Toast.show(t('两次输入的密码不一致'))
      return
    }

    const [err] = await to(
      contactApi.passwordReset({
        account: account,
        code: code,
        password: passwordMD5,
      }),
    )

    if (err) {
      return
    }

    Toast.show(t('密码修改成功 请用新密码登录'))
    setPageType('login')
  }

  /*
   * 更新配置信息
   * @params {object} options
   */
  async function updataConfig(params: any) {
    const { setting, corpId } = params || {}

    // 获取公共配置
    const [err, res] = await to(basicApi.getLogistics({ corpId }))
    const cod = (!err && res?.data) || {}

    if (!setting) {
      return
    }

    /**
     * cloudStoreFlag
     *
     * 1:向游客开放
     * 2:不显示价格
     * 2<<1:仅老客户显示价格
     * 2<<2:老客户和游客都显示价格
     * 2<<3:老客户优先报上次价
     * 2<<4:不显示销量
     * 2<<5:显示销售数量
     * 2<<6:显示销售次数
     * 2<<7:自动上架
     * 2<<8:客户下单强制支付
     * 2<<9:云店显示库存
     * 2<<10:小程序快速开单配置
     * 2<<11: 有货才可以下单
     * 2<<12: 游客下单强制支付
     * 2<<13:在线支付单据默认打印
     * 2<<14:是否允许下载图片
     * 2<<15:小程序电子单显示云店入口
     * 2<<16:库存0自动上架
     * 2<<18:购买权限
     *
     */
    Object.assign(corpSetting, {
      ...handleCorpSetting(setting),
      cod,
    })

    setCorpSetting(corpSetting)
  }

  // 获取用已有账号登录、新用户注册、忘记密码等按钮
  function getAuxiliaryBtns() {
    let btns: JSX.Element[] = []
    if (pageType === 'register' || pageType === 'forgetPassword') {
      btns.push(
        <span
          key="login"
          className={styles['bottom-btn']}
          onClick={() => setPageType('login')}
        >
          {t('用已有账号登录')}
        </span>,
      )
    } else if (pageType === 'login') {
      btns = [
        ...btns,
        ...[
          <span
            key="register"
            className={styles['bottom-btn']}
            onClick={() => setPageType('register')}
          >
            {t('新用户注册')}
          </span>,
          <span
            key="forgetPassword"
            className={styles['bottom-btn']}
            onClick={() => setPageType('forgetPassword')}
          >
            {t('忘记密码')}
          </span>,
        ],
      ]
    }
    return btns
  }

  function onZoneNumberChange(data: ICountryNumber) {
    setZoneNumber(data)
    setZoneNumberVisible(false)
    const zoneNumberLabel = `${data.telephoneCode}（${data.countryName}）`
    setLocalStorage('zoneNumber', zoneNumberLabel)
  }

  const auxiliaryBtns = getAuxiliaryBtns()
  const inMallApp = !(window.webview && window.webview.getAuth)

  return (
    <div className={styles['login-page']}>
      <div className={styles['container']}>
        {pageType !== 'forgetPassword' ? (
          <Tabs
            className={styles['tabs']}
            activeKey={accountType}
            onChange={setAccountType}
          >
            <Tabs.Tab title={t('手机登录')} key="1"></Tabs.Tab>
            <Tabs.Tab title={t('邮箱登录')} key="2"></Tabs.Tab>
          </Tabs>
        ) : (
          <div className={styles['forget-password-title']}></div>
        )}

        <div className={styles['input-section']}>
          <div className={styles['input-box']}>
            <span className={styles['base-input-wrap']}>
              {accountType === AccountType.PHONE &&
                zoneNumber.telephoneCode && (
                  <span
                    className={styles['zone-number']}
                    onClick={() => setZoneNumberVisible(true)}
                  >{`${zoneNumber.telephoneCode}（${zoneNumber.countryName}）`}</span>
                )}
              {accountType === AccountType.PHONE && (
                <DownOutline
                  className={styles['zone-number-icon']}
                  onClick={() => setZoneNumberVisible(true)}
                  fontSize={14}
                />
              )}
              <Input
                ref={inputRef}
                className={styles['base-input']}
                value={formData.account}
                onChange={(value) => setFormData({ account: value })}
                placeholder={inputAccountText}
              />
            </span>

            {pageType === 'forgetPassword' && (
              <span className={styles['base-input-wrap']}>
                <Input
                  className={`${styles['base-input']} ${styles['no-bottom-border']} ${styles['flex1']}`}
                  value={formData.code}
                  onChange={(value) => setFormData({ code: value })}
                  placeholder={t('请输入验证码')}
                />

                <span className={styles['code-text']} onClick={getCode}>
                  {codeText}
                </span>
              </span>
            )}

            <Input
              className={styles['base-input']}
              type="password"
              value={formData.password}
              onChange={(value) => setFormData({ password: value })}
              placeholder={inputPassWordText}
            />

            {(pageType === 'register' || pageType === 'forgetPassword') && (
              <Input
                className={styles['base-input']}
                type="password"
                value={formData.passwordAgain}
                onChange={(value) => setFormData({ passwordAgain: value })}
                placeholder={inputPassWordTextAgain}
              />
            )}
          </div>

          <div className={styles['policy-tip']}>
            {/* <img
              src={ isAgree ? checkBoxCheckedIcon : checkBoxIcon }
              className={ styles["check-circle"] }
              onClick= { () => setIsAgree(!isAgree) }
              /> */}
            <Checkbox
              className={styles['check-circle']}
              checked={isAgree}
              onChange={setIsAgree}
            />
            <span>
              {t('已阅读并同意')}
              <a className={styles['policy-text']}> {t('用户协议')} </a>
              {t('和')}
              <a className={styles['policy-text']}> {t('隐私政策')} </a>
            </span>
          </div>

          <Button
            color="primary"
            className={styles['confirm-btn']}
            onClick={onConfirm}
          >
            {btnText}
          </Button>

          <div className={styles['bottom-opection']}>
            <div className={styles['bottom-btn-box']}>{auxiliaryBtns}</div>
            <div className={styles['bottom-select-box']}>
              {/* 选择站点，仅限mall app可用 */}
              {inMallApp ? (
                <Popover.Menu
                  placement="bottom-end"
                  trigger="click"
                  actions-direction="vertical"
                  actions={siteList}
                  onAction={(item: Action) => setSite(item)}
                >
                  <div>
                    <span
                      className={`${styles['bottom-btn']} ${styles['lang-btn']} ${styles['vertical-middle']}`}
                      style={{ marginRight: '4px' }}
                    >
                      {siteOpt?.text}
                    </span>
                    <img
                      src={arrowDown}
                      className={`${styles['lang-arrow']} ${styles['vertical-middle']}`}
                    />
                  </div>
                </Popover.Menu>
              ) : null}

              {/* 选择语言 */}
              <Popover.Menu
                placement="bottom-end"
                trigger="click"
                actions-direction="vertical"
                actions={langOptions}
                onAction={(item: Action) => onSelectLang(item)}
              >
                <div>
                  <span
                    className={`${styles['bottom-btn']} ${styles['lang-btn']} ${styles['vertical-middle']}`}
                    style={{ marginRight: '4px' }}
                  >
                    {langOpt.text}
                  </span>
                  <img
                    src={arrowDown}
                    className={`${styles['lang-arrow']} ${styles['vertical-middle']}`}
                  />
                </div>
              </Popover.Menu>
            </div>
          </div>
        </div>
      </div>

      <div className={styles['footer-btn-box']}>
        <img src={paifaLogoText} className={styles['paifa-logo']} />
        <div className={styles['footer-btn']} onClick={onTempLogin}>
          {t('体验一下')}
          <RightOutline fontSize={16} style={{ marginLeft: '4px' }} />
        </div>
      </div>

      <ZoneNumberList
        visible={zoneNumberVisible}
        list={countryList}
        onClose={() => setZoneNumberVisible(false)}
        onChange={onZoneNumberChange}
      ></ZoneNumberList>
    </div>
  )
}

export default Login
